import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Textarea,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import {
  FeedbackSource,
  FeedbackType,
  IngestionDocumentValidationFeedbackFragment,
} from '@client/graphql/__generated__/types';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { FC, useEffect, useState } from 'react';
import { useComplianceFeedback } from './hooks/useIngestionDocumentFeedback';

interface BaseDocumentValidationFeedbackProps {
  ingestionDocumentValidationId: string;
  ruleUuid: string;
  contractDocumentUuid: string;
  allFeedback: IngestionDocumentValidationFeedbackFragment[];
  isHovered: boolean;
}

interface ExternalDocumentValidationFeedbackProps
  extends BaseDocumentValidationFeedbackProps {
  variant: 'external';
}

interface InternalDocumentValidationFeedbackProps
  extends BaseDocumentValidationFeedbackProps {
  variant: 'internal';
}

export type DocumentValidationFeedbackProps =
  | ExternalDocumentValidationFeedbackProps
  | InternalDocumentValidationFeedbackProps;

export const DocumentValidationFeedback: FC<
  DocumentValidationFeedbackProps
> = ({
  ingestionDocumentValidationId,
  ruleUuid,
  contractDocumentUuid,
  allFeedback,
  isHovered,
  variant,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [comment, setComment] = useState('');
  const [selectedFeedbackType, setSelectedFeedbackType] =
    useState<FeedbackType | null>(null);

  // Filter feedback for this specific rule, contract document, and variant
  const existingFeedback = allFeedback.find(
    (feedback) =>
      feedback.ruleUuid === ruleUuid &&
      feedback.contractDocumentUuid === contractDocumentUuid &&
      feedback.source ===
        (variant === 'external'
          ? FeedbackSource.EXTERNAL
          : FeedbackSource.INTERNAL)
  );

  const { createOrUpdateFeedback, createOrUpdateFeedbackLoading } =
    useComplianceFeedback({
      firstIngestionDocumentValidationId: ingestionDocumentValidationId,
      variant,
    });

  const handleSubmitFeedback = () => {
    if (!selectedFeedbackType) return;

    createOrUpdateFeedback(
      {
        ingestionDocumentValidationId,
        ruleUuid,
        contractDocumentUuid,
        feedbackType: selectedFeedbackType,
        comment: comment.trim() || undefined,
      },
      onClose
    );
  };

  const handleFeedbackTypeSelect = (feedbackType: FeedbackType) => {
    setSelectedFeedbackType(feedbackType);
    // Always open popover for comment
    onOpen();
  };

  const handleClose = () => {
    setComment(existingFeedback?.comment || '');
    setSelectedFeedbackType(existingFeedback?.feedbackType || null);
    onClose();
  };

  useEffect(() => {
    if (existingFeedback?.feedbackType) {
      setSelectedFeedbackType(existingFeedback.feedbackType);
    } else {
      setSelectedFeedbackType(null);
    }
    if (existingFeedback?.comment) {
      setComment(existingFeedback.comment);
    } else {
      setComment('');
    }
  }, [existingFeedback, contractDocumentUuid, ruleUuid]);

  return (
    <Box
      visibility={
        isHovered || existingFeedback?.feedbackType ? 'visible' : 'hidden'
      }
    >
      <Flex>
        <IconButton
          aria-label="Thumbs up"
          color={
            selectedFeedbackType === FeedbackType.THUMBS_UP
              ? 'compliance.button.secondary.text'
              : 'compliance.text.muted'
          }
          icon={<ThumbsUp size={16} />}
          size="xs"
          variant="link"
          onClick={() => handleFeedbackTypeSelect(FeedbackType.THUMBS_UP)}
        />
        <IconButton
          aria-label="Thumbs down"
          color={
            selectedFeedbackType === FeedbackType.THUMBS_DOWN
              ? 'compliance.button.secondary.text'
              : 'compliance.text.muted'
          }
          icon={<ThumbsDown size={16} />}
          size="xs"
          variant="link"
          onClick={() => handleFeedbackTypeSelect(FeedbackType.THUMBS_DOWN)}
        />
      </Flex>

      <Popover isOpen={isOpen} placement="bottom-end" onClose={handleClose}>
        <PopoverTrigger>
          <Box />
        </PopoverTrigger>
        <Portal>
          <PopoverContent>
            <PopoverArrow />
            <PopoverBody>
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel color="compliance.text.secondary" size="sm">
                    {selectedFeedbackType === FeedbackType.THUMBS_UP
                      ? 'Tell us what worked well (optional)'
                      : 'Tell us what could be improved (optional)'}
                  </FormLabel>
                  <Textarea
                    placeholder={
                      selectedFeedbackType === FeedbackType.THUMBS_UP
                        ? 'What did you like about this validation?'
                        : 'What could be improved?'
                    }
                    rows={3}
                    size="sm"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                </FormControl>
                <HStack spacing={2} w="full">
                  <Button
                    flex={1}
                    size="sm"
                    variant="outline"
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    colorScheme="blue"
                    flex={1}
                    isLoading={createOrUpdateFeedbackLoading}
                    size="sm"
                    onClick={() => void handleSubmitFeedback()}
                  >
                    Submit
                  </Button>
                </HStack>
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    </Box>
  );
};
